import { addDisplayName, addHandlers, addProps, flowMax } from 'ad-hok'
import cx from 'classnames'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import Masonry, { MasonryOptions } from 'react-masonry-component'
import { UserListPublic } from 'shared/src/api/types'
import { addNavigate } from 'shared/src/utils/addNavigate'
import { formatDate } from 'shared/src/utils/formatting'

import { ProductViews } from 'components/ProductViews'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { makeEditUserListRoute } from '../../utils/routes'
import Icon from '../Icon'
import {
  BUTTONS_STYLE,
  MAIN_TITLE_STYLE,
  NOTES_BOLD_STYLE,
  NOTES_MEDIUM_STYLE,
  SERIF_FONT_FAMILY,
} from '../styles'

const GUTTER = 16

const makeMasonryOptions = (item: string, stamp: string): MasonryOptions => ({
  itemSelector: `.${item}`,
  stamp: `.${stamp}`,
  transitionDuration: 0,
  gutter: GUTTER,
  percentPosition: true,
})

interface StyleProps {
  coverImageUrl?: string
}

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: ({ coverImageUrl }: StyleProps) => (coverImageUrl ? 166 : 86),
  },
  title: {
    ...MAIN_TITLE_STYLE,
    marginBottom: 8,
  },
  subtitle: {
    ...NOTES_BOLD_STYLE,
    maxWidth: 208,
    color: '#90734F',
  },
  masonaryContainer: {
    paddingLeft: 16,
    paddingRight: 16,
    position: 'relative',
    top: ({ coverImageUrl }: StyleProps) => (coverImageUrl ? 166 : 86),
  },
  header: {
    display: 'flex',
    position: 'absolute',
    width: '100%',
    paddingTop: 16,
    marginTop: ({ coverImageUrl }: StyleProps) => (coverImageUrl ? 16 : 0),
    justifyContent: 'space-between',
    background: ({ coverImageUrl }: StyleProps) =>
      coverImageUrl
        ? `linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), url(${coverImageUrl}) no-repeat center center/cover`
        : null,
    height: ({ coverImageUrl }: StyleProps) => (coverImageUrl ? 268 : 'auto'),
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 32,
    paddingRight: 32,
    maxWidth: 300,
  },
  name: {
    fontFamily: SERIF_FONT_FAMILY,
    fontWeight: 800,
    fontStyle: 'italic',
    fontSize: 28,
    lineHeight: '140%',
    letterSpacing: '0.01em',
    color: 'rgba(203, 166, 122, 0.56)',
    marginBottom: 4,
    display: '-webkit-box',
    '-webkit-line-clamp': 1,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  nameWithCoverImage: {
    fontSize: 34,
    lineHeight: '100%',
    letterSpacing: 0.48,
    color: '#FFF',
    marginBottom: 8,
  },
  dateRow: {
    flexDirection: 'row',
    display: 'flex',
  },
  date: {
    ...NOTES_MEDIUM_STYLE,
    color: ({ coverImageUrl }: StyleProps) =>
      coverImageUrl ? 'rgba(255, 255, 255, 0.8)' : 'rgba(54, 52, 50, 0.8)',
  },
  editButton: {
    ...BUTTONS_STYLE,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: ({ coverImageUrl }: StyleProps) =>
      coverImageUrl ? '#FFF' : '#90734F',
    height: 48,
    paddingLeft: 2,
    paddingRight: 31,
  },
  editButtonIcon: {
    color: ({ coverImageUrl }: StyleProps) =>
      coverImageUrl ? '#FFF' : '#90734F',
  },
  newButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 48,
    paddingLeft: 24,
    paddingRight: 24,
    backgroundColor: 'rgba(54, 52, 50, 0.96)',
    ...BUTTONS_STYLE,
    color: 'white',
  },
  newButtonIcon: {
    marginRight: 8,
  },
  stamp: {
    position: 'absolute',
  },
  stampLeft: {
    top: 0,
    left: 0,
    width: 50,
    height: 30,
  },
  stampRight: {
    top: 0,
    right: 0,
    width: 50,
    height: 15,
  },
  product: {
    marginBottom: 35,
    width: `calc(33% - ${GUTTER}px * 2 / 3)`,

    '@media (max-width: 376px)': {
      width: `calc(50% - ${GUTTER}px)`,
    },
  },
  overlay: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
  views: {
    position: 'absolute',
    left: 8,
    top: 4,
  },
  image: {
    width: '100%',
    backgroundColor: '#ffffff',
  },
})

interface Props {
  list: UserListPublic
  userId?: number
}

export const Edit: FC<Props> = flowMax(
  addDisplayName('Edit'),
  addNavigate,
  addHandlers({
    onEdit:
      ({ list, userId, navigate }) =>
      () => {
        navigate(makeEditUserListRoute(list.id, userId, true))
      },
  }),
  addProps(({ list: { createdAt } }) => ({
    date: formatDate(createdAt),
  })),
  addProps(({ list: { edit } }) => ({
    classes: useStyles({ coverImageUrl: edit?.coverImage?.full }),
  })),
  addTranslationHelpers,
  ({ list: { name, products, edit, isPublic }, date, onEdit, classes, t }) => (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <div
            className={cx(
              classes.name,
              edit?.coverImage && classes.nameWithCoverImage
            )}
          >
            {edit?.name ?? name}
          </div>
          <div className={classes.dateRow}>
            <div className={classes.date}>{date}</div>
            {!isPublic && (
              <div className={classes.date}>{t('admin.userEdits.private')}</div>
            )}
          </div>
        </div>
        <button onClick={onEdit} className={classes.editButton}>
          <Icon name="editFlipped" className={classes.editButtonIcon} />
          {t('admin.userEdits.edit')}
        </button>
      </div>
      <div className={classes.masonaryContainer}>
        <Masonry options={makeMasonryOptions(classes.product, classes.stamp)}>
          <div className={cx(classes.stamp, classes.stampLeft)} />
          <div className={cx(classes.stamp, classes.stampRight)} />
          {products.map((product) => (
            <div className={classes.product} key={product.id}>
              <img
                src={product.image.thumbnail}
                alt={product.name}
                className={classes.image}
              />
              <div className={classes.overlay} />
              <ProductViews
                views={product.views ?? 0}
                className={classes.views}
              />
            </div>
          ))}
        </Masonry>
      </div>
    </div>
  )
)
